import React, { useState, useRef, useEffect } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * CodeArtifactViewer Component
 *
 * Displays code artifacts with:
 * - Syntax highlighting (Prism-based)
 * - Line numbers
 * - Copy button with feedback
 * - Language detection/display
 * - Scrollable for long code
 * - Inline editing mode
 */
function CodeArtifactViewer({ content, language = 'javascript', title, isEditing = false, onContentChange }) {
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef(null);

  // Normalize language name for syntax highlighter
  const normalizeLanguage = (lang) => {
    const languageMap = {
      'js': 'javascript',
      'ts': 'typescript',
      'py': 'python',
      'rb': 'ruby',
      'yml': 'yaml',
      'md': 'markdown',
      'sh': 'bash',
      'shell': 'bash',
      'zsh': 'bash',
      'jsx': 'jsx',
      'tsx': 'tsx',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'json': 'json',
      'sql': 'sql',
      'go': 'go',
      'rust': 'rust',
      'java': 'java',
      'c': 'c',
      'cpp': 'cpp',
      'csharp': 'csharp',
      'cs': 'csharp',
      'php': 'php',
      'swift': 'swift',
      'kotlin': 'kotlin',
      'scala': 'scala',
      'r': 'r',
      'lua': 'lua',
      'perl': 'perl',
      'elixir': 'elixir',
      'haskell': 'haskell',
      'clojure': 'clojure',
      'dockerfile': 'dockerfile',
      'docker': 'dockerfile',
      'makefile': 'makefile',
      'graphql': 'graphql',
      'xml': 'xml',
      'vue': 'vue',
      'svelte': 'javascript',
    };

    const normalizedLang = (lang || '').toLowerCase().trim();
    return languageMap[normalizedLang] || normalizedLang || 'text';
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const normalizedLanguage = normalizeLanguage(language);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // Place cursor at end
      textareaRef.current.setSelectionRange(
        textareaRef.current.value.length,
        textareaRef.current.value.length
      );
    }
  }, [isEditing]);

  // Handle content changes in edit mode
  const handleChange = (e) => {
    if (onContentChange) {
      onContentChange(e.target.value);
    }
  };

  // Calculate line numbers for editor
  const lineCount = (content || '').split('\n').length;

  return (
    <div className="h-full flex flex-col">
      {/* Code header with language and copy button */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <span className="text-xs text-gray-400 font-mono uppercase tracking-wide">
          {normalizedLanguage}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-400 hover:text-white
            hover:bg-gray-700 rounded transition-colors"
          title={copied ? 'Copied!' : 'Copy code'}
        >
          {copied ? (
            <>
              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-green-500">Copied!</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      {/* Code content - either editable textarea or syntax highlighted view */}
      <div className="flex-1 overflow-auto bg-gray-900">
        {isEditing ? (
          // Edit mode - show textarea with line numbers
          <div className="flex h-full">
            {/* Line numbers column */}
            <div className="shrink-0 py-4 pl-4 pr-0 text-right text-gray-500 font-mono text-sm select-none"
              style={{ minWidth: '3em', lineHeight: '1.6' }}
            >
              {Array.from({ length: lineCount }, (_, i) => (
                <div key={i + 1}>{i + 1}</div>
              ))}
            </div>
            {/* Editable textarea */}
            <textarea
              ref={textareaRef}
              value={content || ''}
              onChange={handleChange}
              className="flex-1 bg-transparent text-gray-100 font-mono text-sm p-4 pl-4 resize-none focus:outline-hidden"
              style={{
                lineHeight: '1.6',
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                tabSize: 2,
              }}
              spellCheck={false}
              placeholder="// Enter your code here..."
            />
          </div>
        ) : (
          // View mode - syntax highlighted
          <SyntaxHighlighter
            language={normalizedLanguage}
            style={oneDark}
            showLineNumbers={true}
            wrapLines={true}
            lineNumberStyle={{
              minWidth: '3em',
              paddingRight: '1em',
              textAlign: 'right',
              color: '#6b7280',
              userSelect: 'none',
            }}
            customStyle={{
              margin: 0,
              padding: '1rem',
              fontSize: '0.875rem',
              lineHeight: '1.6',
              background: 'transparent',
              minHeight: '100%',
            }}
            codeTagProps={{
              style: {
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              }
            }}
          >
            {content || '// No code content'}
          </SyntaxHighlighter>
        )}
      </div>
    </div>
  );
}

export default CodeArtifactViewer;
